import { useMemo, type ReactNode } from "react";
import { useClaims, useSettingUp, useUsage } from "@/lib/hooks/billing";
import { useEnvoy } from "@/lib/hooks/envoy";
import { tailorsLeft } from "@/lib/overview";
import { memberStanding, VISITOR, type Standing } from "@/lib/standing";
import { StandingContext } from "./standingContext";

/** A visitor's standing, for the pages of someone signed out. */
export function VisitorStanding({ children }: { children: ReactNode }) {
    return <StandingContext.Provider value={VISITOR}>{children}</StandingContext.Provider>;
}

/** A signed-in person's standing, from billing and envoy. Only under a session. */
export function MemberStanding({ children }: { children: ReactNode }) {
    const claims = useClaims();
    const usage = useUsage();
    const envoy = useEnvoy();
    const settingUp = useSettingUp();
    const tier = claims.data?.entitlements.tier;
    const tailors = usage.data ? tailorsLeft(usage.data) : null;
    const standing = useMemo(
        () => memberStanding({ tier, envoy: envoy.data, tailors, settingUp }),
        // The figures, not the objects: a refetch with the same answer keeps the frame still.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tier, envoy.data, tailors?.left, tailors?.limit, settingUp],
    );
    return <StandingContext.Provider value={standing}>{children}</StandingContext.Provider>;
}

/** A standing given outright, for a test of what the frame draws in each state. */
export function StandingContextForTests({
    value,
    children,
}: {
    value: Standing;
    children: ReactNode;
}) {
    return <StandingContext.Provider value={value}>{children}</StandingContext.Provider>;
}
